import { Header, List, Loader } from '@mairistem/composants';
import { isEmpty } from 'lodash';
import React from 'react';
import type ResultatTransfertComptabilite from '../../../../interfaces/simulation/resultatTransfertComptabilite';
import {
	getEspaces,
	getImputationsComptabilite,
	getLignesEspace,
} from '../../../../services/simulation/elementComptabilite';
import {
	getLibelleIntrouvable,
	getLibellesRapport,
	getResumeTransfert,
	type LibellesRapport,
} from '../../../../utils/simulation/rapportTransfertComptabilite';
import RapportJob from '../RapportJob';

interface RapportTransfertComptabiliteProps {
	// Annee de l'exercice de la simulation (N), les elements sont crees sur N+1
	anneeExercice?: number;
	resultat: ResultatTransfertComptabilite;
}

const SECTIONS: { key: keyof ResultatTransfertComptabilite; titre: string }[] =
	[
		{ key: 'imputations', titre: 'Imputations' },
		{ key: 'espaces', titre: 'Espaces' },
		{ key: 'lignesEspace', titre: "Lignes d'espace" },
	];

// Un appel en echec n'empeche pas d'afficher le rapport : ses elements ont un libelle de repli
const orEmpty = <T,>(promise: Promise<Map<number, T>>) =>
	promise.catch(() => new Map<number, T>());

/**
 * Recupere par lot les elements crees (les lignes d'espace incluent leur
 * imputation et leur espace avec son service) puis construit leurs libelles
 */
const fetchLibelles = async (
	resultat: ResultatTransfertComptabilite,
): Promise<LibellesRapport> => {
	const [imputations, espaces, lignesEspace] = await Promise.all([
		orEmpty(getImputationsComptabilite(resultat.imputations)),
		orEmpty(getEspaces(resultat.espaces)),
		orEmpty(getLignesEspace(resultat.lignesEspace)),
	]);

	return getLibellesRapport(resultat, { espaces, imputations, lignesEspace });
};

const RapportTransfertComptabilite = ({
	anneeExercice,
	resultat,
}: RapportTransfertComptabiliteProps) => {
	const [libelles, setLibelles] = React.useState<LibellesRapport>();

	const sections = React.useMemo(
		() => SECTIONS.filter(({ key }) => !isEmpty(resultat[key])),
		[resultat],
	);

	React.useEffect(() => {
		if (isEmpty(sections)) return;

		let cancelled = false;
		fetchLibelles(resultat).then((data) => {
			if (!cancelled) setLibelles(data);
		});

		return () => {
			cancelled = true;
		};
	}, [resultat, sections]);

	return (
		<RapportJob
			resume={getResumeTransfert(resultat, anneeExercice)}
			testIdResume="resumeTransfert"
			titreDetail="Détail des éléments créés"
		>
			{!isEmpty(sections) &&
				(libelles ? (
					sections.map(({ key, titre }) => (
						<React.Fragment key={key}>
							<Header content={titre} size="tiny" />
							<List
								bulleted
								items={resultat[key].map((identifiant: number) => ({
									content:
										libelles[key][identifiant] ??
										getLibelleIntrouvable(identifiant),
									key: identifiant,
								}))}
							/>
						</React.Fragment>
					))
				) : (
					<Loader active inline="centered" size="small" />
				))}
		</RapportJob>
	);
};

export default RapportTransfertComptabilite;
